import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { z } from 'zod'
import { toApiError } from '@/api/errors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { fetchApplicationStatus, publicAdmissionsQuery } from '@/features/admissions/api'
import { PublicAdmissionShell } from '@/features/admissions/components/PublicAdmissionShell'
import { admissionStatusLabel, admissionStatusTone } from '@/features/admissions/status'
import type { PublicApplicationStatus } from '@/features/admissions/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate } from '@/utils/format'

const schema = z.object({
  reference: z.string().trim().min(3, 'Enter the reference of the application.'),
  email: z.email('Enter a valid email address.'),
})
type LookupForm = z.infer<typeof schema>

/** /inscription/suivi: families follow their application with its reference and their email. */
export function AdmissionStatusPage() {
  const { t } = useTranslation('admissions')
  useDocumentTitle('Follow an application')
  const [params] = useSearchParams()
  const info = useQuery(publicAdmissionsQuery())
  const [status, setStatus] = useState<PublicApplicationStatus | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LookupForm>({
    resolver: zodResolver(schema),
    defaultValues: { reference: params.get('ref') ?? '', email: '' },
  })

  const onSubmit = handleSubmit(async (v) => {
    setFailure(null)
    try {
      setStatus(await fetchApplicationStatus(v.reference, v.email))
    } catch (e) {
      setStatus(null)
      setFailure(toApiError(e).message)
    }
  })

  return (
    <PublicAdmissionShell school={info.data?.school ?? null} title={t('Follow an application')}>
      <Card className="p-4 sm:p-6">
        <form onSubmit={(e) => void onSubmit(e)} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={t('Reference')} error={errors.reference?.message} required>
            <Input placeholder="INS-2027-0001" autoCapitalize="characters" {...register('reference')} />
          </FormField>
          <FormField
            label={t('Email address given on the application')}
            error={errors.email?.message}
            required
          >
            <Input type="email" autoComplete="email" {...register('email')} />
          </FormField>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              leftIcon={<Search />}
              loading={isSubmitting}
              className="h-auto min-h-10 w-full py-2 whitespace-normal sm:w-auto"
            >
              {t('See where it stands')}
            </Button>
          </div>
        </form>
        {failure ? (
          <Alert tone="danger" className="mt-4">
            {failure}
          </Alert>
        ) : null}
        {status ? (
          <section
            aria-live="polite"
            className="mt-5 rounded-card border border-line bg-surface-muted/50 p-4"
          >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h2 className="min-w-0 flex-1 font-semibold break-words text-ink">
                {status.student.firstName} {status.student.lastName}
              </h2>
              <Badge tone={admissionStatusTone[status.status]} dot>
                {t(admissionStatusLabel[status.status])}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted">
              {status.reference} · {status.className} · {status.academicYear} ·{' '}
              {t('sent on {{date}}', { date: formatDate(status.submittedAt) })}
            </p>
            <p className="mt-3 text-sm text-ink-soft">{status.message}</p>
            {status.reason ? (
              <p className="mt-2 text-sm text-ink-soft">
                {t('Reason: {{reason}}', { reason: status.reason })}
              </p>
            ) : null}
          </section>
        ) : null}
        <p className="mt-6 text-sm">
          <Link to="/inscription" className="font-medium text-brand-700 hover:underline">
            {t('Back to the application form')}
          </Link>
        </p>
      </Card>
    </PublicAdmissionShell>
  )
}
